// skills/architecting/architectingData.js
// Architecting blueprints, milestones, and civic contracts

import { SKILLS } from '../../game/constants.js';

// ==================== BALANCE REFERENCE: CHEST STORAGE ====================
//
// PET LOOT CHEST (Husbandry):
//   Unique items possible: ~20 (from all pet contract reward pools, expandable via Beastmaster's Lodge)
//   Slots: Base 6 → +4/tier (Pet Quarters I/II/III) + 2/tier (Beastmaster's Lodge I/II/III) → Max 24 slots
//   Stack: Base 25 → +25/+50/+100 (Pet Pantry I/II/III at Lv 30/55/80) → Max 200 per slot
//   Speed: +2% per Pet Pantry tier (6% total)
//   Total capacity at max: 18 slots × 200 stack = 3,600 items
//
// GUILD CHEST (Pilfering):
//   Unique items possible: 36+ (7 domains × ~5 items each, expandable via Pilferer's Network)
//   Slots: Base 8 → +2 per 10 arch levels + 2/tier (Pilferer's Network I/II/III) → Max 30 slots
//   Stack: Base 25 → +25/+50/+100 (Guild Vault I/II/III at Lv 35/60/85) → Max 200 per slot
//   Speed: +2% per Guild Vault tier (6% total)
//   Total capacity at max: 24 slots × 200 stack = 4,800 items
//
// TIER UPGRADES (unlock higher-tier items in pools):
//   - Pilferer's Network I/II/III at Lv 40/60/80 (requires matching Pilfering level)
//   - Beastmaster's Lodge I/II/III at Lv 40/60/80 (requires matching Husbandry level)
//
// ARCHITECT'S SANCTUM (Lv 99):
//   Requires all 6 tier upgrades. Auto-deposits both chests to bank. Seeds to BloomHold first.
//
// FILL RATES (for timer estimates):
//   - Pets: Variable duration contracts (45-120 min), 1-5 items per completion
//   - Pilferers: ~60-90s per item depending on pilfering level
//
// MIGRATION NOTE:
//   Existing saves with more items than new limits will show "overflow" state.
//   Items are preserved, but new items can't be added until claimed below limit.
//
// ==================== BLUEPRINTS ====================
// Replaces old BUILDINGS - now requires materials

export const BLUEPRINTS = {
    // ===== TIER 1 (Level 1-20): Common Timber + Pyrestone Bar =====
    // Blueprints are client commissions - XP only, no item drops
    WOODEN_CHAIR: {
        id: 'wooden_chair',
        name: 'Rickety Chair',
        tier: 1,
        level: 1,
        xp: 2,
        baseTime: 30 * 1000, // 30 seconds - active quick build
        materials: { common_timber: 3, pyrestone_bar: 1 },
        gold: 0,
        buildMode: 'active',
        townsperson: 'Unsold',
        color: '#8b4513'
    },
    WOODEN_TABLE: {
        id: 'wooden_table',
        name: 'Wooden Table',
        tier: 1,
        level: 5,
        xp: 220,
        baseTime: 43 * 60 * 1000, // 43 minutes
        materials: { common_timber: 12, pyrestone_bar: 4 },
        gold: 100,
        townsperson: 'Carpenter',
        color: '#a0522d'
    },
    STORAGE_CRATE: {
        id: 'storage_crate',
        name: 'Storage Crate',
        tier: 1,
        level: 10,
        xp: 300,
        baseTime: 46 * 60 * 1000, // 46 minutes
        materials: { common_timber: 15, pyrestone_bar: 5 },
        gold: 200,
        townsperson: 'Merchant',
        color: '#cd853f'
    },
    WOODEN_FENCE: {
        id: 'wooden_fence',
        name: 'Wooden Fence',
        tier: 1,
        level: 15,
        xp: 400,
        baseTime: 48 * 60 * 1000, // 48 minutes
        materials: { common_timber: 20, pyrestone_bar: 6 },
        gold: 350,
        townsperson: 'Farmer',
        color: '#deb887'
    },
    SIMPLE_BED: {
        id: 'simple_bed',
        name: 'Simple Bed',
        tier: 1,
        level: 18,
        xp: 480,
        baseTime: 50 * 60 * 1000, // 50 minutes
        materials: { common_timber: 25, pyrestone_bar: 8 },
        gold: 500,
        townsperson: 'Innkeeper',
        color: '#d2691e'
    },
    WOODEN_SHELF: {
        id: 'wooden_shelf',
        name: 'Wooden Shelf',
        tier: 1,
        level: 3,
        xp: 180,
        baseTime: 41 * 60 * 1000, // 41 minutes
        materials: { common_timber: 10, pyrestone_bar: 2 },
        gold: 50,
        townsperson: 'Shopkeeper',
        color: '#a0522d'
    },
    TOOL_RACK: {
        id: 'tool_rack',
        name: 'Tool Rack',
        tier: 1,
        level: 7,
        xp: 250,
        baseTime: 44 * 60 * 1000, // 44 minutes
        materials: { common_timber: 14, pyrestone_bar: 4 },
        gold: 120,
        townsperson: 'Blacksmith',
        color: '#696969'
    },
    BARREL_STAND: {
        id: 'barrel_stand',
        name: 'Barrel Stand',
        tier: 1,
        level: 12,
        xp: 340,
        baseTime: 47 * 60 * 1000, // 47 minutes
        materials: { common_timber: 18, pyrestone_bar: 5 },
        gold: 250,
        townsperson: 'Brewer',
        color: '#8b4513'
    },
    WINDOW_FRAME: {
        id: 'window_frame',
        name: 'Window Frame',
        tier: 1,
        level: 14,
        xp: 380,
        baseTime: 48 * 60 * 1000, // 48 minutes
        materials: { common_timber: 16, pyrestone_bar: 6 },
        gold: 300,
        townsperson: 'Glazier',
        color: '#87ceeb'
    },
    WOODEN_CART: {
        id: 'wooden_cart',
        name: 'Wooden Cart',
        tier: 1,
        level: 17,
        xp: 450,
        baseTime: 49 * 60 * 1000, // 49 minutes
        materials: { common_timber: 22, pyrestone_bar: 7 },
        gold: 450,
        townsperson: 'Wheelwright',
        color: '#deb887'
    },

    // ===== TIER 2 (Level 20-40): Ironwood + Blackiron Bar =====
    IRONWOOD_WARDROBE: {
        id: 'ironwood_wardrobe',
        name: 'Ironwood Wardrobe',
        tier: 2,
        level: 20,
        xp: 600,
        baseTime: 55 * 60 * 1000, // 55 minutes
        materials: { ironwood_logs: 15, blackiron_bar: 5 },
        gold: 800,
        townsperson: 'Noble',
        color: '#8b4513'
    },
    MARKET_STALL: {
        id: 'market_stall',
        name: 'Market Stall',
        tier: 2,
        level: 25,
        xp: 750,
        baseTime: 60 * 60 * 1000, // 60 minutes (1 hour)
        materials: { ironwood_logs: 20, blackiron_bar: 8 },
        gold: 1200,
        townsperson: 'Merchant',
        color: '#a0522d'
    },
    FORGE_BELLOWS: {
        id: 'forge_bellows',
        name: 'Forge Bellows',
        tier: 2,
        level: 30,
        xp: 900,
        baseTime: 65 * 60 * 1000, // 65 minutes
        materials: { ironwood_logs: 25, blackiron_bar: 12 },
        gold: 1500,
        townsperson: 'Blacksmith',
        color: '#696969'
    },
    WATCHTOWER_BASE: {
        id: 'watchtower_base',
        name: 'Watchtower Base',
        tier: 2,
        level: 35,
        xp: 1100,
        baseTime: 70 * 60 * 1000, // 70 minutes
        materials: { ironwood_logs: 30, blackiron_bar: 15 },
        gold: 2000,
        townsperson: 'Guard Captain',
        color: '#4a4a4a'
    },
    IRONWOOD_CHEST: {
        id: 'ironwood_chest',
        name: 'Ironwood Chest',
        tier: 2,
        level: 22,
        xp: 650,
        baseTime: 57 * 60 * 1000, // 57 minutes
        materials: { ironwood_logs: 18, blackiron_bar: 6 },
        gold: 900,
        townsperson: 'Treasurer',
        color: '#8b4513'
    },
    DISPLAY_CABINET: {
        id: 'display_cabinet',
        name: 'Display Cabinet',
        tier: 2,
        level: 27,
        xp: 820,
        baseTime: 62 * 60 * 1000, // 62 minutes
        materials: { ironwood_logs: 22, blackiron_bar: 10 },
        gold: 1400,
        townsperson: 'Collector',
        color: '#daa520'
    },
    WEAPON_RACK: {
        id: 'weapon_rack',
        name: 'Weapon Rack',
        tier: 2,
        level: 32,
        xp: 950,
        baseTime: 67 * 60 * 1000, // 67 minutes
        materials: { ironwood_logs: 26, blackiron_bar: 14 },
        gold: 1700,
        townsperson: 'Armorer',
        color: '#708090'
    },
    STORAGE_SHELVING: {
        id: 'storage_shelving',
        name: 'Storage Shelving',
        tier: 2,
        level: 37,
        xp: 1050,
        baseTime: 68 * 60 * 1000, // 68 minutes
        materials: { ironwood_logs: 28, blackiron_bar: 12 },
        gold: 1800,
        townsperson: 'Warehouse Master',
        color: '#a0522d'
    },
    WORK_BENCH: {
        id: 'work_bench',
        name: 'Work Bench',
        tier: 2,
        level: 38,
        xp: 1080,
        baseTime: 69 * 60 * 1000, // 69 minutes
        materials: { ironwood_logs: 32, blackiron_bar: 16 },
        gold: 1900,
        townsperson: 'Craftsman',
        color: '#cd853f'
    },
    GATE_FRAME: {
        id: 'gate_frame',
        name: 'Gate Frame',
        tier: 2,
        level: 39,
        xp: 1150,
        baseTime: 70 * 60 * 1000, // 70 minutes
        materials: { ironwood_logs: 35, blackiron_bar: 18 },
        gold: 2100,
        townsperson: 'Gatekeeper',
        color: '#4a4a4a'
    },

    // ===== TIER 3 (Level 40-60): Softbark + Skyiron Bar =====
    ORNATE_THRONE: {
        id: 'ornate_throne',
        name: 'Ornate Throne',
        tier: 3,
        level: 40,
        xp: 1400,
        baseTime: 75 * 60 * 1000, // 75 minutes
        materials: { softbark_logs: 20, skyiron_bar: 10 },
        gold: 3000,
        townsperson: 'Royal Carpenter',
        color: '#b8860b'
    },
    LIBRARY_SHELVING: {
        id: 'library_shelving',
        name: 'Library Shelving',
        tier: 3,
        level: 45,
        xp: 1700,
        baseTime: 80 * 60 * 1000, // 80 minutes
        materials: { softbark_logs: 25, skyiron_bar: 12 },
        gold: 4000,
        townsperson: 'Scholar',
        color: '#8b4513'
    },
    ALTAR_PLATFORM: {
        id: 'altar_platform',
        name: 'Altar Platform',
        tier: 3,
        level: 50,
        xp: 2000,
        baseTime: 85 * 60 * 1000, // 85 minutes
        materials: { softbark_logs: 30, skyiron_bar: 15 },
        gold: 5000,
        townsperson: 'Priest',
        color: '#daa520'
    },
    GREENHOUSE_FRAME: {
        id: 'greenhouse_frame',
        name: 'Greenhouse Frame',
        tier: 3,
        level: 55,
        xp: 2400,
        baseTime: 90 * 60 * 1000, // 90 minutes
        materials: { softbark_logs: 35, skyiron_bar: 18 },
        gold: 6000,
        townsperson: 'Botanist',
        color: '#228b22'
    },
    ORNATE_DESK: {
        id: 'ornate_desk',
        name: 'Ornate Desk',
        tier: 3,
        level: 42,
        xp: 1500,
        baseTime: 77 * 60 * 1000, // 77 minutes
        materials: { softbark_logs: 22, skyiron_bar: 11 },
        gold: 3200,
        townsperson: 'Scribe',
        color: '#8b4513'
    },
    GARDEN_TRELLIS: {
        id: 'garden_trellis',
        name: 'Garden Trellis',
        tier: 3,
        level: 47,
        xp: 1800,
        baseTime: 82 * 60 * 1000, // 82 minutes
        materials: { softbark_logs: 28, skyiron_bar: 14 },
        gold: 4200,
        townsperson: 'Gardener',
        color: '#228b22'
    },
    FOUNTAIN_BASE: {
        id: 'fountain_base',
        name: 'Fountain Base',
        tier: 3,
        level: 52,
        xp: 2100,
        baseTime: 86 * 60 * 1000, // 86 minutes
        materials: { softbark_logs: 32, skyiron_bar: 16 },
        gold: 5200,
        townsperson: 'Stonemason',
        color: '#4682b4'
    },
    DECORATIVE_ARCHWAY: {
        id: 'decorative_archway',
        name: 'Decorative Archway',
        tier: 3,
        level: 54,
        xp: 2300,
        baseTime: 88 * 60 * 1000, // 88 minutes
        materials: { softbark_logs: 30, skyiron_bar: 17 },
        gold: 5600,
        townsperson: 'Artisan',
        color: '#daa520'
    },
    CARVED_RAILING: {
        id: 'carved_railing',
        name: 'Carved Railing',
        tier: 3,
        level: 57,
        xp: 2500,
        baseTime: 89 * 60 * 1000, // 89 minutes
        materials: { softbark_logs: 38, skyiron_bar: 19 },
        gold: 6200,
        townsperson: 'Woodcarver',
        color: '#a0522d'
    },
    CEREMONIAL_PODIUM: {
        id: 'ceremonial_podium',
        name: 'Ceremonial Podium',
        tier: 3,
        level: 58,
        xp: 2600,
        baseTime: 90 * 60 * 1000, // 90 minutes
        materials: { softbark_logs: 40, skyiron_bar: 20 },
        gold: 6500,
        townsperson: 'Herald',
        color: '#9400d3'
    },

    // ===== TIER 4 (Level 60-80): Gravewood + Verdant Core Bar =====
    PORTAL_ARCH: {
        id: 'portal_arch',
        name: 'Portal Arch',
        tier: 4,
        level: 60,
        xp: 3000,
        baseTime: 95 * 60 * 1000, // 95 minutes
        materials: { gravewood_logs: 25, verdant_core_bar: 12 },
        gold: 8000,
        townsperson: 'Archmage',
        color: '#9370db'
    },
    GRAND_FOUNTAIN: {
        id: 'grand_fountain',
        name: 'Grand Fountain',
        tier: 4,
        level: 65,
        xp: 3600,
        baseTime: 100 * 60 * 1000, // 100 minutes
        materials: { gravewood_logs: 30, verdant_core_bar: 15 },
        gold: 10000,
        townsperson: 'Mason',
        color: '#4682b4'
    },
    COUNCIL_CHAMBERS: {
        id: 'council_chambers',
        name: 'Council Chambers',
        tier: 4,
        level: 70,
        xp: 4200,
        baseTime: 105 * 60 * 1000, // 105 minutes
        materials: { gravewood_logs: 35, verdant_core_bar: 18 },
        gold: 12000,
        townsperson: 'Mayor',
        color: '#8b0000'
    },
    TRAINING_GROUNDS: {
        id: 'training_grounds',
        name: 'Training Grounds',
        tier: 4,
        level: 75,
        xp: 4800,
        baseTime: 110 * 60 * 1000, // 110 minutes
        materials: { gravewood_logs: 40, verdant_core_bar: 20 },
        gold: 15000,
        townsperson: 'Knight Commander',
        color: '#cd5c5c'
    },
    ENCHANTING_TABLE: {
        id: 'enchanting_table',
        name: 'Enchanting Table',
        tier: 4,
        level: 62,
        xp: 3200,
        baseTime: 97 * 60 * 1000, // 97 minutes
        materials: { gravewood_logs: 28, verdant_core_bar: 14 },
        gold: 8500,
        townsperson: 'Enchanter',
        color: '#9370db'
    },
    OBSERVATORY_PLATFORM: {
        id: 'observatory_platform',
        name: 'Observatory Platform',
        tier: 4,
        level: 67,
        xp: 3800,
        baseTime: 102 * 60 * 1000, // 102 minutes
        materials: { gravewood_logs: 32, verdant_core_bar: 16 },
        gold: 10500,
        townsperson: 'Stargazer',
        color: '#191970'
    },
    VAULT_DOOR: {
        id: 'vault_door',
        name: 'Vault Door',
        tier: 4,
        level: 72,
        xp: 4400,
        baseTime: 106 * 60 * 1000, // 106 minutes
        materials: { gravewood_logs: 36, verdant_core_bar: 22 },
        gold: 13000,
        townsperson: 'Locksmith',
        color: '#ffd700'
    },
    THRONE_PLATFORM: {
        id: 'throne_platform',
        name: 'Throne Platform',
        tier: 4,
        level: 74,
        xp: 4600,
        baseTime: 108 * 60 * 1000, // 108 minutes
        materials: { gravewood_logs: 38, verdant_core_bar: 19 },
        gold: 14000,
        townsperson: 'Royal Architect',
        color: '#b8860b'
    },
    DECORATIVE_PILLAR: {
        id: 'decorative_pillar',
        name: 'Decorative Pillar',
        tier: 4,
        level: 77,
        xp: 5000,
        baseTime: 109 * 60 * 1000, // 109 minutes
        materials: { gravewood_logs: 42, verdant_core_bar: 21 },
        gold: 15500,
        townsperson: 'Sculptor',
        color: '#daa520'
    },
    GRAND_STAIRCASE: {
        id: 'grand_staircase',
        name: 'Grand Staircase',
        tier: 4,
        level: 78,
        xp: 5200,
        baseTime: 110 * 60 * 1000, // 110 minutes
        materials: { gravewood_logs: 45, verdant_core_bar: 23 },
        gold: 16000,
        townsperson: 'Master Builder',
        color: '#8b0000'
    },

    // ===== TIER 5 (Level 80-99): Arcanewood + Voidsteel Bar =====
    ARCANE_OBSERVATORY: {
        id: 'arcane_observatory',
        name: 'Arcane Observatory',
        tier: 5,
        level: 80,
        xp: 5500,
        baseTime: 115 * 60 * 1000, // 115 minutes
        materials: { arcanewood_logs: 30, voidsteel_bar: 15 },
        gold: 18000,
        townsperson: 'Astronomer',
        color: '#191970'
    },
    ROYAL_TREASURY: {
        id: 'royal_treasury',
        name: 'Royal Treasury',
        tier: 5,
        level: 85,
        xp: 6500,
        baseTime: 117 * 60 * 1000, // 117 minutes
        materials: { arcanewood_logs: 35, voidsteel_bar: 18 },
        gold: 20000,
        townsperson: 'Treasurer',
        color: '#ffd700'
    },
    CHAMPIONS_HALL: {
        id: 'champions_hall',
        name: 'Champion\'s Hall',
        tier: 5,
        level: 90,
        xp: 7500,
        baseTime: 118 * 60 * 1000, // 118 minutes
        materials: { arcanewood_logs: 40, voidsteel_bar: 22 },
        gold: 22000,
        townsperson: 'Hero',
        color: '#9400d3'
    },
    ETERNAL_MONUMENT: {
        id: 'eternal_monument',
        name: 'Eternal Monument',
        tier: 5,
        level: 95,
        xp: 9000,
        baseTime: 120 * 60 * 1000, // 120 minutes (2 hours)
        materials: { arcanewood_logs: 50, voidsteel_bar: 25 },
        gold: 25000,
        townsperson: 'Historian',
        color: '#00ced1'
    },
    ARCANE_PEDESTAL: {
        id: 'arcane_pedestal',
        name: 'Arcane Pedestal',
        tier: 5,
        level: 82,
        xp: 5800,
        baseTime: 116 * 60 * 1000, // 116 minutes
        materials: { arcanewood_logs: 32, voidsteel_bar: 16 },
        gold: 18500,
        townsperson: 'Arcane Scholar',
        color: '#9370db'
    },
    RUNIC_GATEWAY: {
        id: 'runic_gateway',
        name: 'Runic Gateway',
        tier: 5,
        level: 87,
        xp: 7000,
        baseTime: 117 * 60 * 1000, // 117 minutes
        materials: { arcanewood_logs: 38, voidsteel_bar: 20 },
        gold: 21000,
        townsperson: 'Runesmith',
        color: '#00ced1'
    },
    CRYSTAL_DISPLAY: {
        id: 'crystal_display',
        name: 'Crystal Display',
        tier: 5,
        level: 92,
        xp: 8000,
        baseTime: 118 * 60 * 1000, // 118 minutes
        materials: { arcanewood_logs: 42, voidsteel_bar: 23 },
        gold: 23000,
        townsperson: 'Crystal Keeper',
        color: '#e0ffff'
    },
    ETERNAL_BRAZIER: {
        id: 'eternal_brazier',
        name: 'Eternal Flame Brazier',
        tier: 5,
        level: 94,
        xp: 8500,
        baseTime: 119 * 60 * 1000, // 119 minutes
        materials: { arcanewood_logs: 45, voidsteel_bar: 24 },
        gold: 24000,
        townsperson: 'Flame Keeper',
        color: '#ff4500'
    },
    MASTERS_WORKBENCH: {
        id: 'masters_workbench',
        name: 'Master\'s Workbench',
        tier: 5,
        level: 97,
        xp: 9500,
        baseTime: 120 * 60 * 1000, // 120 minutes
        materials: { arcanewood_logs: 55, voidsteel_bar: 28 },
        gold: 26000,
        townsperson: 'Grand Architect',
        color: '#daa520'
    },
    LEGACY_SHRINE: {
        id: 'legacy_shrine',
        name: 'Legacy Shrine',
        tier: 5,
        level: 98,
        xp: 10000,
        baseTime: 120 * 60 * 1000, // 120 minutes
        materials: { arcanewood_logs: 60, voidsteel_bar: 30 },
        gold: 28000,
        townsperson: 'Keeper of Legacies',
        color: '#ffd700'
    }
};

// ==================== TOOLS ====================

export const TOOLS = {
    PYRESTONE: {
        id: 'pyrestone_hammer',
        name: 'Pyrestone Hammer',
        level: 1,
        speedMultiplier: 1.0,
        price: 0
    },
    BLACKIRON: {
        id: 'blackiron_hammer',
        name: 'Blackiron Hammer',
        level: 15,
        speedMultiplier: 1.2,
        price: 1000
    },
    PALE_SHARD: {
        id: 'pale_shard_hammer',
        name: 'Pale Shard Hammer',
        level: 30,
        speedMultiplier: 1.4,
        price: 5000
    },
    SKYIRON: {
        id: 'skyiron_hammer',
        name: 'Skyiron Hammer',
        level: 45,
        speedMultiplier: 1.6,
        price: 25000
    },
    VERDANT: {
        id: 'verdant_hammer',
        name: 'Verdant Hammer',
        level: 60,
        speedMultiplier: 1.8,
        price: 100000
    },
    VOIDSTEEL: {
        id: 'voidsteel_hammer',
        name: 'Voidsteel Hammer',
        level: 75,
        speedMultiplier: 2.0,
        price: 250000
    }
};

// ==================== MILESTONES ====================
// One-time permanent unlocks that enable game features

export const MILESTONES = {
    MESS_HALL: {
        id: 'mess_hall',
        name: 'Mess Hall',
        level: 5,
        icon: '🍽️',
        description: 'Unlocks the Auto-Eat system for combat. Requires Combat Lv 5 to activate.',
        materials: { common_timber: 50, pyrestone_bar: 20 },
        gold: 1000,
        buildTime: 60 * 60 * 1000, // 60 minutes (1 hour)
        xp: 500,
        effect: { type: 'unlock', target: 'autoEat' },
        prerequisite: null
    },
    REINFORCED_STORAGE_I: {
        id: 'reinforced_storage_i',
        name: 'Reinforced Storage I',
        level: 15,
        icon: '📦',
        description: 'Increases bank storage by 50 slots',
        materials: { common_timber: 80, pyrestone_bar: 30 },
        gold: 2500,
        buildTime: 65 * 60 * 1000, // 65 minutes
        xp: 800,
        effect: { type: 'storage', target: 'bank', bonus: 50 },
        prerequisite: null
    },
    GUILD_HALL: {
        id: 'guild_hall',
        name: 'Guild Hall',
        level: 20,
        icon: '🏛️',
        description: 'Unlocks the Pilfering Guild for passive loot gathering.',
        materials: { common_timber: 100, blackiron_bar: 25 },
        gold: 5000,
        buildTime: 70 * 60 * 1000, // 70 minutes
        xp: 1200,
        effect: { type: 'unlock', target: 'pilferingGuild' },
        prerequisite: null
    },
    PET_QUARTERS_I: {
        id: 'pet_quarters_i',
        name: 'Pet Quarters I',
        level: 25,
        icon: '🐾',
        description: 'Increases pet loot chest storage by 4 slots.',
        materials: { ironwood_logs: 60, blackiron_bar: 20 },
        gold: 4000,
        buildTime: 75 * 60 * 1000, // 75 minutes
        xp: 1000,
        effect: { type: 'storage', target: 'petChest', bonus: 4 },
        prerequisite: null
    },
    // ============ STACK SIZE MILESTONES ============
    // Stack limits control how many items fit per chest slot
    // Base: 25 per slot → Tier I: 50 → Tier II: 100 → Tier III: 200
    PET_STORAGE_I: {
        id: 'pet_storage_i',
        name: 'Pet Pantry I',
        level: 30,
        icon: '🥫',
        description: 'Increases pet loot chest stack limit by +25 per slot. Pet contracts complete 2% faster.',
        materials: { ironwood_logs: 80, blackiron_bar: 25 },
        gold: 8000,
        buildTime: 78 * 60 * 1000, // 78 minutes
        xp: 1100,
        effect: [
            { type: 'stack', target: 'petChest', bonus: 25 },
            { type: 'speed', target: 'petContracts', bonus: 0.02 }
        ],
        prerequisite: 'pet_quarters_i'
    },
    GREENHOUSE: {
        id: 'greenhouse',
        name: 'Greenhouse',
        level: 35,
        icon: '🌿',
        description: 'Unlocks BloomHold: seed storage, plant from BloomHold, and Plant All',
        materials: { softbark_logs: 80, skyiron_bar: 30 },
        gold: 8000,
        buildTime: 80 * 60 * 1000, // 80 minutes
        xp: 1800,
        effect: { type: 'unlock', target: 'bloomhold' },
        prerequisite: null
    },
    GUILD_STORAGE_I: {
        id: 'guild_storage_i',
        name: 'Guild Vault I',
        level: 35,
        icon: '🗄️',
        description: 'Increases guild chest stack limit by +25 per slot. Pilferers work 2% faster.',
        materials: { softbark_logs: 60, skyiron_bar: 25 },
        gold: 10000,
        buildTime: 78 * 60 * 1000, // 78 minutes
        xp: 1500,
        effect: [
            { type: 'stack', target: 'guildChest', bonus: 25 },
            { type: 'speed', target: 'pilferers', bonus: 0.02 }
        ],
        prerequisite: 'guild_hall'
    },
    REINFORCED_STORAGE_II: {
        id: 'reinforced_storage_ii',
        name: 'Reinforced Storage II',
        level: 40,
        icon: '📦',
        description: 'Increases bank storage by 100 slots',
        materials: { softbark_logs: 100, skyiron_bar: 40 },
        gold: 12000,
        buildTime: 85 * 60 * 1000, // 85 minutes
        xp: 2500,
        effect: { type: 'storage', target: 'bank', bonus: 100 },
        prerequisite: 'reinforced_storage_i'
    },
    // ============ TIER UPGRADE MILESTONES ============
    // Unlock higher-tier items in Pilfering Guild and Husbandry contract pools
    // Require both Architecting AND the relevant skill level
    PILFERERS_NETWORK_I: {
        id: 'pilferers_network_i',
        name: 'Pilferer\'s Network I',
        level: 40,
        secondarySkill: SKILLS.PILFERING,
        secondaryLevel: 40,
        icon: '🕸️',
        description: 'Expands pilferer contacts to gather mid-tier resources. +2 guild chest slots.',
        materials: { softbark_logs: 100, skyiron_bar: 40 },
        gold: 25000,
        buildTime: 90 * 60 * 1000, // 90 minutes
        xp: 3000,
        effect: [
            { type: 'pilferingTier', tier: 1 },
            { type: 'storage', target: 'guildChest', bonus: 2 }
        ],
        prerequisite: 'guild_hall'
    },
    BEASTMASTERS_LODGE_I: {
        id: 'beastmasters_lodge_i',
        name: 'Beastmaster\'s Lodge I',
        level: 40,
        secondarySkill: SKILLS.HUSBANDRY,
        secondaryLevel: 40,
        icon: '🦁',
        description: 'Trains pets to find mid-tier materials on contracts. +2 pet chest slots.',
        materials: { softbark_logs: 100, skyiron_bar: 40 },
        gold: 25000,
        buildTime: 90 * 60 * 1000, // 90 minutes
        xp: 3000,
        effect: [
            { type: 'husbandryTier', tier: 1 },
            { type: 'storage', target: 'petChest', bonus: 2 }
        ],
        prerequisite: null
    },
    BLUEPRINT_STUDIO_I: {
        id: 'blueprint_studio_i',
        name: 'Blueprint Studio I',
        level: 45,
        icon: '📐',
        description: 'Reduces blueprint build time by 10%',
        materials: { softbark_logs: 120, skyiron_bar: 50 },
        gold: 15000,
        buildTime: 90 * 60 * 1000, // 90 minutes
        xp: 3000,
        effect: { type: 'speed', target: 'blueprints', bonus: 0.10 },
        prerequisite: null
    },
    PET_QUARTERS_II: {
        id: 'pet_quarters_ii',
        name: 'Pet Quarters II',
        level: 50,
        icon: '🐾',
        description: 'Increases pet chest storage by 4 slots',
        materials: { gravewood_logs: 80, verdant_core_bar: 25 },
        gold: 18000,
        buildTime: 95 * 60 * 1000, // 95 minutes
        xp: 3500,
        effect: { type: 'storage', target: 'petChest', bonus: 4 },
        prerequisite: 'pet_quarters_i'
    },
    APOTHECARY_STATION: {
        id: 'apothecary_station',
        name: 'Apothecary Station',
        level: 25,
        icon: '🧪',
        description: 'Unlocks the Apothecary Belt for auto-potion usage in combat (separate belt per equipment set)',
        materials: { ironwood_logs: 60, blackiron_bar: 20, clean_emberroot: 5 },
        gold: 6000,
        buildTime: 75 * 60 * 1000, // 75 minutes
        xp: 1200,
        effect: { type: 'unlock', target: 'apothecaryBelt' },
        prerequisite: 'mess_hall'
    },
    BLOOMHOLD_EXPANSION: {
        id: 'bloomhold_expansion',
        name: 'BloomHold Expansion',
        level: 55,
        icon: '🌸',
        description: 'Increases BloomHold storage by 50 slots',
        materials: { gravewood_logs: 100, verdant_core_bar: 30 },
        gold: 20000,
        buildTime: 100 * 60 * 1000, // 100 minutes
        xp: 4000,
        effect: { type: 'storage', target: 'bloomhold', bonus: 50 },
        prerequisite: 'greenhouse'
    },
    PET_STORAGE_II: {
        id: 'pet_storage_ii',
        name: 'Pet Pantry II',
        level: 55,
        icon: '🥫',
        description: 'Increases pet loot chest stack limit by +50 per slot. Pet contracts complete 2% faster.',
        materials: { gravewood_logs: 80, verdant_core_bar: 25 },
        gold: 30000,
        buildTime: 98 * 60 * 1000, // 98 minutes
        xp: 3800,
        effect: [
            { type: 'stack', target: 'petChest', bonus: 50 },
            { type: 'speed', target: 'petContracts', bonus: 0.02 }
        ],
        prerequisite: 'pet_storage_i'
    },
    GUILD_STORAGE_II: {
        id: 'guild_storage_ii',
        name: 'Guild Vault II',
        level: 60,
        icon: '🗄️',
        description: 'Increases guild chest stack limit by +50 per slot. Pilferers work 2% faster.',
        materials: { gravewood_logs: 100, verdant_core_bar: 30 },
        gold: 35000,
        buildTime: 100 * 60 * 1000, // 100 minutes
        xp: 4200,
        effect: [
            { type: 'stack', target: 'guildChest', bonus: 50 },
            { type: 'speed', target: 'pilferers', bonus: 0.02 }
        ],
        prerequisite: 'guild_storage_i'
    },
    PILFERERS_NETWORK_II: {
        id: 'pilferers_network_ii',
        name: 'Pilferer\'s Network II',
        level: 60,
        secondarySkill: SKILLS.PILFERING,
        secondaryLevel: 60,
        icon: '🕸️',
        description: 'Expands pilferer contacts to gather high-tier resources. +2 guild chest slots.',
        materials: { gravewood_logs: 120, verdant_core_bar: 50 },
        gold: 60000,
        buildTime: 100 * 60 * 1000, // 100 minutes
        xp: 5000,
        effect: [
            { type: 'pilferingTier', tier: 2 },
            { type: 'storage', target: 'guildChest', bonus: 2 }
        ],
        prerequisite: 'pilferers_network_i'
    },
    BEASTMASTERS_LODGE_II: {
        id: 'beastmasters_lodge_ii',
        name: 'Beastmaster\'s Lodge II',
        level: 60,
        secondarySkill: SKILLS.HUSBANDRY,
        secondaryLevel: 60,
        icon: '🦁',
        description: 'Trains pets to find high-tier materials on contracts. +2 pet chest slots.',
        materials: { gravewood_logs: 120, verdant_core_bar: 50 },
        gold: 60000,
        buildTime: 100 * 60 * 1000, // 100 minutes
        xp: 5000,
        effect: [
            { type: 'husbandryTier', tier: 2 },
            { type: 'storage', target: 'petChest', bonus: 2 }
        ],
        prerequisite: 'beastmasters_lodge_i'
    },
    REINFORCED_STORAGE_III: {
        id: 'reinforced_storage_iii',
        name: 'Reinforced Storage III',
        level: 70,
        icon: '📦',
        description: 'Increases bank storage by 150 slots',
        materials: { gravewood_logs: 150, verdant_core_bar: 50 },
        gold: 35000,
        buildTime: 105 * 60 * 1000, // 105 minutes
        xp: 5500,
        effect: { type: 'storage', target: 'bank', bonus: 150 },
        prerequisite: 'reinforced_storage_ii'
    },
    PET_QUARTERS_III: {
        id: 'pet_quarters_iii',
        name: 'Pet Quarters III',
        level: 75,
        icon: '🐾',
        description: 'Increases pet chest storage by 4 slots',
        materials: { arcanewood_logs: 100, voidsteel_bar: 30 },
        gold: 40000,
        buildTime: 110 * 60 * 1000, // 110 minutes
        xp: 6000,
        effect: { type: 'storage', target: 'petChest', bonus: 4 },
        prerequisite: 'pet_quarters_ii'
    },
    PET_STORAGE_III: {
        id: 'pet_storage_iii',
        name: 'Pet Pantry III',
        level: 80,
        icon: '🥫',
        description: 'Increases pet loot chest stack limit by +100 per slot. Pet contracts complete 2% faster.',
        materials: { arcanewood_logs: 120, voidsteel_bar: 35 },
        gold: 75000,
        buildTime: 112 * 60 * 1000, // 112 minutes
        xp: 7000,
        effect: [
            { type: 'stack', target: 'petChest', bonus: 100 },
            { type: 'speed', target: 'petContracts', bonus: 0.02 }
        ],
        prerequisite: 'pet_storage_ii'
    },
    PILFERERS_NETWORK_III: {
        id: 'pilferers_network_iii',
        name: 'Pilferer\'s Network III',
        level: 80,
        secondarySkill: SKILLS.PILFERING,
        secondaryLevel: 80,
        icon: '🕸️',
        description: 'Expands pilferer contacts to gather the highest-tier resources. +2 guild chest slots.',
        materials: { arcanewood_logs: 150, voidsteel_bar: 60 },
        gold: 120000,
        buildTime: 115 * 60 * 1000, // 115 minutes
        xp: 8000,
        effect: [
            { type: 'pilferingTier', tier: 3 },
            { type: 'storage', target: 'guildChest', bonus: 2 }
        ],
        prerequisite: 'pilferers_network_ii'
    },
    BEASTMASTERS_LODGE_III: {
        id: 'beastmasters_lodge_iii',
        name: 'Beastmaster\'s Lodge III',
        level: 80,
        secondarySkill: SKILLS.HUSBANDRY,
        secondaryLevel: 80,
        icon: '🦁',
        description: 'Trains pets to find the highest-tier materials on contracts. +2 pet chest slots.',
        materials: { arcanewood_logs: 150, voidsteel_bar: 60 },
        gold: 120000,
        buildTime: 115 * 60 * 1000, // 115 minutes
        xp: 8000,
        effect: [
            { type: 'husbandryTier', tier: 3 },
            { type: 'storage', target: 'petChest', bonus: 2 }
        ],
        prerequisite: 'beastmasters_lodge_ii'
    },
    BLUEPRINT_STUDIO_II: {
        id: 'blueprint_studio_ii',
        name: 'Blueprint Studio II',
        level: 85,
        icon: '📐',
        description: 'Reduces blueprint build time by an additional 15%',
        materials: { arcanewood_logs: 150, voidsteel_bar: 45 },
        gold: 60000,
        buildTime: 115 * 60 * 1000, // 115 minutes
        xp: 7500,
        effect: { type: 'speed', target: 'blueprints', bonus: 0.15 },
        prerequisite: 'blueprint_studio_i'
    },
    GUILD_STORAGE_III: {
        id: 'guild_storage_iii',
        name: 'Guild Vault III',
        level: 85,
        icon: '🗄️',
        description: 'Increases guild chest stack limit by +100 per slot. Pilferers work 2% faster.',
        materials: { arcanewood_logs: 140, voidsteel_bar: 40 },
        gold: 80000,
        buildTime: 114 * 60 * 1000, // 114 minutes
        xp: 7200,
        effect: [
            { type: 'stack', target: 'guildChest', bonus: 100 },
            { type: 'speed', target: 'pilferers', bonus: 0.02 }
        ],
        prerequisite: 'guild_storage_ii'
    },
    ARCHITECTS_SANCTUM: {
        id: 'architects_sanctum',
        name: 'Architect\'s Sanctum',
        level: 99,
        icon: '🏰',
        description: 'Pilfering Guild and Pet Loot chests automatically deposit to bank. Seeds route to BloomHold first.',
        materials: { arcanewood_logs: 300, voidsteel_bar: 100 },
        gold: 1000000,
        buildTime: 180 * 60 * 1000, // 180 minutes (3 hours)
        xp: 50000,
        effect: { type: 'unlock', target: 'autoLootChests' },
        prerequisites: ['pilferers_network_iii', 'beastmasters_lodge_iii']
    }
};

// ==================== CIVIC CONTRACTS ====================
// Multi-phase long-duration projects with valuable rewards

export const CIVIC_CONTRACTS = {
    // ===== UNCOMMON (Level 30+) =====
    TOWN_BRIDGE: {
        id: 'town_bridge',
        name: 'Town Bridge Restoration',
        level: 30,
        rarity: 'uncommon',
        icon: '🌉',
        description: 'Restore the old town bridge to allow easier trade routes',
        phases: [
            {
                name: 'Foundation',
                type: 'build',
                duration: 45 * 60 * 1000,
                materials: { ironwood_logs: 40, blackiron_bar: 20 },
                gold: 2000,
                xp: 800
            },
            {
                name: 'Curing',
                type: 'wait',
                duration: 60 * 60 * 1000,
                materials: {},
                gold: 0,
                xp: 0
            },
            {
                name: 'Assembly',
                type: 'build',
                duration: 60 * 60 * 1000,
                materials: { ironwood_logs: 50, blackiron_bar: 25 },
                gold: 2500,
                xp: 1200
            },
            {
                name: 'Finishing',
                type: 'build',
                duration: 30 * 60 * 1000,
                materials: { ironwood_logs: 20, blackiron_bar: 10 },
                gold: 1000,
                xp: 500
            }
        ],
        rewards: {
            gold: 15000,
            xp: 3000,
            items: []
        }
    },
    MARKET_RENOVATION: {
        id: 'market_renovation',
        name: 'Market Square Renovation',
        level: 35,
        rarity: 'uncommon',
        icon: '🏪',
        description: 'Renovate the town market to attract more merchants',
        phases: [
            {
                name: 'Demolition',
                type: 'build',
                duration: 30 * 60 * 1000,
                materials: { ironwood_logs: 30 },
                gold: 1500,
                xp: 600
            },
            {
                name: 'Construction',
                type: 'build',
                duration: 90 * 60 * 1000,
                materials: { softbark_logs: 60, skyiron_bar: 20 },
                gold: 4000,
                xp: 1500
            },
            {
                name: 'Decoration',
                type: 'build',
                duration: 45 * 60 * 1000,
                materials: { softbark_logs: 30, skyiron_bar: 10 },
                gold: 2000,
                xp: 800
            }
        ],
        rewards: {
            gold: 18000,
            xp: 3500,
            items: []
        }
    },
    WATCHTOWER_NETWORK: {
        id: 'watchtower_network',
        name: 'Watchtower Network',
        level: 40,
        rarity: 'uncommon',
        icon: '🗼',
        description: 'Build a network of watchtowers for town defense',
        phases: [
            {
                name: 'Site Preparation',
                type: 'build',
                duration: 40 * 60 * 1000,
                materials: { softbark_logs: 40, skyiron_bar: 15 },
                gold: 2500,
                xp: 900
            },
            {
                name: 'Tower Construction',
                type: 'build',
                duration: 120 * 60 * 1000,
                materials: { softbark_logs: 80, skyiron_bar: 40 },
                gold: 6000,
                xp: 2500
            },
            {
                name: 'Signal System',
                type: 'build',
                duration: 30 * 60 * 1000,
                materials: { skyiron_bar: 20 },
                gold: 1500,
                xp: 600
            }
        ],
        rewards: {
            gold: 22000,
            xp: 4500,
            items: []
        }
    },

    // ===== RARE (Level 50+) =====
    GRAND_LIBRARY: {
        id: 'grand_library',
        name: 'Grand Library Construction',
        level: 50,
        rarity: 'rare',
        icon: '📚',
        description: 'Build a grand library to house ancient knowledge',
        phases: [
            {
                name: 'Foundation',
                type: 'build',
                duration: 60 * 60 * 1000,
                materials: { gravewood_logs: 50, verdant_core_bar: 20 },
                gold: 5000,
                xp: 1500
            },
            {
                name: 'Walls & Roof',
                type: 'build',
                duration: 90 * 60 * 1000,
                materials: { gravewood_logs: 80, verdant_core_bar: 35 },
                gold: 8000,
                xp: 2500
            },
            {
                name: 'Curing',
                type: 'wait',
                duration: 90 * 60 * 1000,
                materials: {},
                gold: 0,
                xp: 0
            },
            {
                name: 'Interior',
                type: 'build',
                duration: 60 * 60 * 1000,
                materials: { gravewood_logs: 40, verdant_core_bar: 15 },
                gold: 4000,
                xp: 1500
            },
            {
                name: 'Shelving',
                type: 'build',
                duration: 45 * 60 * 1000,
                materials: { gravewood_logs: 30, verdant_core_bar: 10 },
                gold: 3000,
                xp: 1000
            }
        ],
        rewards: {
            gold: 40000,
            xp: 8000,
            items: [{ itemId: 'bookcase', quantity: 5 }]
        }
    },
    TEMPLE_RESTORATION: {
        id: 'temple_restoration',
        name: 'Ancient Temple Restoration',
        level: 60,
        rarity: 'rare',
        icon: '⛩️',
        description: 'Restore the ancient temple to its former glory',
        phases: [
            {
                name: 'Excavation',
                type: 'build',
                duration: 45 * 60 * 1000,
                materials: { gravewood_logs: 30, verdant_core_bar: 15 },
                gold: 4000,
                xp: 1200
            },
            {
                name: 'Structural Repair',
                type: 'build',
                duration: 120 * 60 * 1000,
                materials: { gravewood_logs: 100, verdant_core_bar: 50 },
                gold: 12000,
                xp: 4000
            },
            {
                name: 'Consecration',
                type: 'wait',
                duration: 120 * 60 * 1000,
                materials: {},
                gold: 0,
                xp: 0
            },
            {
                name: 'Altar Construction',
                type: 'build',
                duration: 60 * 60 * 1000,
                materials: { arcanewood_logs: 40, voidsteel_bar: 20 },
                gold: 8000,
                xp: 2500
            }
        ],
        rewards: {
            gold: 50000,
            xp: 10000,
            items: [{ itemId: 'altar', quantity: 2 }]
        }
    },
    HARBOR_EXPANSION: {
        id: 'harbor_expansion',
        name: 'Harbor Expansion',
        level: 65,
        rarity: 'rare',
        icon: '⚓',
        description: 'Expand the harbor to accommodate larger ships',
        phases: [
            {
                name: 'Dredging',
                type: 'build',
                duration: 60 * 60 * 1000,
                materials: { gravewood_logs: 40 },
                gold: 5000,
                xp: 1500
            },
            {
                name: 'Pier Construction',
                type: 'build',
                duration: 90 * 60 * 1000,
                materials: { gravewood_logs: 80, verdant_core_bar: 40 },
                gold: 10000,
                xp: 3000
            },
            {
                name: 'Warehouse Building',
                type: 'build',
                duration: 75 * 60 * 1000,
                materials: { arcanewood_logs: 50, voidsteel_bar: 25 },
                gold: 8000,
                xp: 2500
            }
        ],
        rewards: {
            gold: 55000,
            xp: 11000,
            items: []
        }
    },

    // ===== LEGENDARY (Level 80+) =====
    ROYAL_PALACE: {
        id: 'royal_palace',
        name: 'Royal Palace Wing',
        level: 80,
        rarity: 'legendary',
        icon: '👑',
        description: 'Construct a new wing for the royal palace',
        phases: [
            {
                name: 'Foundation',
                type: 'build',
                duration: 90 * 60 * 1000,
                materials: { arcanewood_logs: 100, voidsteel_bar: 40 },
                gold: 15000,
                xp: 4000
            },
            {
                name: 'Walls',
                type: 'build',
                duration: 120 * 60 * 1000,
                materials: { arcanewood_logs: 150, voidsteel_bar: 60 },
                gold: 20000,
                xp: 6000
            },
            {
                name: 'Curing',
                type: 'wait',
                duration: 180 * 60 * 1000,
                materials: {},
                gold: 0,
                xp: 0
            },
            {
                name: 'Roofing',
                type: 'build',
                duration: 90 * 60 * 1000,
                materials: { arcanewood_logs: 80, voidsteel_bar: 35 },
                gold: 12000,
                xp: 3500
            },
            {
                name: 'Interior',
                type: 'build',
                duration: 60 * 60 * 1000,
                materials: { arcanewood_logs: 50, voidsteel_bar: 20 },
                gold: 8000,
                xp: 2500
            },
            {
                name: 'Decoration',
                type: 'build',
                duration: 45 * 60 * 1000,
                materials: { voidsteel_bar: 15 },
                gold: 5000,
                xp: 1500
            }
        ],
        rewards: {
            gold: 100000,
            xp: 20000,
            items: [{ itemId: 'throne', quantity: 2 }]
        }
    },
    ARCANE_ACADEMY: {
        id: 'arcane_academy',
        name: 'Arcane Academy',
        level: 90,
        rarity: 'legendary',
        icon: '🔮',
        description: 'Build a prestigious academy for magical studies',
        phases: [
            {
                name: 'Warding',
                type: 'build',
                duration: 60 * 60 * 1000,
                materials: { arcanewood_logs: 80, voidsteel_bar: 30 },
                gold: 12000,
                xp: 3500
            },
            {
                name: 'Main Hall',
                type: 'build',
                duration: 150 * 60 * 1000,
                materials: { arcanewood_logs: 200, voidsteel_bar: 80 },
                gold: 30000,
                xp: 8000
            },
            {
                name: 'Arcane Infusion',
                type: 'wait',
                duration: 240 * 60 * 1000,
                materials: {},
                gold: 0,
                xp: 0
            },
            {
                name: 'Dormitories',
                type: 'build',
                duration: 90 * 60 * 1000,
                materials: { arcanewood_logs: 100, voidsteel_bar: 40 },
                gold: 15000,
                xp: 4500
            },
            {
                name: 'Observatory',
                type: 'build',
                duration: 75 * 60 * 1000,
                materials: { arcanewood_logs: 60, voidsteel_bar: 30 },
                gold: 10000,
                xp: 3000
            }
        ],
        rewards: {
            gold: 150000,
            xp: 30000,
            items: [{ itemId: 'portal', quantity: 3 }]
        }
    }
};

// ==================== CONFIGURATION ====================

export const BLUEPRINT_TIER_NAMES = {
    1: 'Apprentice',
    2: 'Journeyman',
    3: 'Artisan',
    4: 'Expert',
    5: 'Master'
};

export const CONTRACT_SPAWN_CONFIG = {
    BASE_CHANCE: 0.05,      // 5% chance on blueprint completion
    CHANCE_PER_10_LEVELS: 0.02, // +2% per 10 architecting levels
    MAX_CHANCE: 0.15        // 15% max
};

export const RARITY_COLORS = {
    uncommon: '#1eff00',
    rare: '#0070dd',
    legendary: '#ff8000'
};

// Helper to get blueprints by tier
export function getBlueprintsByTier(tier) {
    return Object.values(BLUEPRINTS).filter(b => b.tier === tier).sort((a, b) => a.level - b.level);
}

// Helper to get available civic contracts by level
export function getAvailableCivicContracts(level) {
    return Object.values(CIVIC_CONTRACTS).filter(c => c.level <= level);
}

export default {
    BLUEPRINTS,
    TOOLS,
    MILESTONES,
    CIVIC_CONTRACTS,
    BLUEPRINT_TIER_NAMES,
    CONTRACT_SPAWN_CONFIG,
    RARITY_COLORS,
    getBlueprintsByTier,
    getAvailableCivicContracts
};
